import React from 'react';
import styled from 'styled-components';
import Markdown from 'react-markdown';
import { Body5 } from '@ui';
import { FOLLOW_UP_QUESTION_IDENTIFIER } from '@constants';
import { MoreDetailEnhanced } from './MoreDetail';
import { Working } from '@ui/Working';
import { AddingCitations, Citation } from './Citation';
import { logger } from '@utils/usefulFuncs';
import { parseJSONResultCompanyList } from '@utils/result';
import { AnswerFormatter } from '@classes/AnswerFormatter';

const MarkdownStyling = styled.span`
  pre {
    background-color: ${({ theme }) => theme.themeColors.lowContrastBorder};
    border-left: 3px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    color: ${({ theme }) => theme.themeColors.text};
    border-radius: 2px;

    margin-bottom: 1.6em;
    padding: 1em 1.5em;

    overflow: scroll;
    overflow-x: scroll;
    page-break-inside: avoid;

    * {
      line-height: 1.6;
    }
  }

  code {
    padding: 0 0.2em;

    .comment {
      /* Gray */
      color: #708090;
    }

    .string {
      /* Green */
      color: #0f9d58;
    }

    .keyword {
      /* Blue */
      color: #0086b3;
    }

    .function {
      /* Orange */
      color: #f08c00;
    }
  }

  td,
  th {
    padding: 8px;
  }

  th {
    background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  }

  td {
    border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  }

  th,
  td {
    color: ${({ theme }) => theme.themeColors.text};
    border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
    border-radius: 1px;
  }

  th {
    font-size: 12px;
    line-height: 14px;
    min-width: 120px;
  }

  ol,
  ul {
    padding-top: 8px;
    padding-bottom: 4px;
  }

  li {
    min-height: 28px;
    padding: 0 0 0 2px;
    margin-bottom: 12px;

    > span:first-child {
      display: block;
    }

    p {
      margin: 0;
    }
  }
`;

export function removeFollowUpQuestions(result) {
  try {
    if (!result) return result;
    return result.split(FOLLOW_UP_QUESTION_IDENTIFIER)[0];
  } catch (e) {
    logger('could not remove follow-up questions', e, result);
    return result;
  }
}

// Function to fix LaTeX delimiter issues - convert bracket notation to $$
export function fixLatexDelimiters(markdownText) {
  let fixed = markdownText;

  // Fix [ \begin ... ] to $$\begin ... $$
  fixed = fixed.replace(/\[\s*(\\begin\{[^}]+\}[\s\S]*?\\end\{[^}]+\})\s*\]/g, (match, content) => {
    const escaped = content.replace(/\$/g, '\\$');
    return `$$\n${escaped}\n$$`;
  });

  // Handle single brackets around align environments
  fixed = fixed.replace(/\[\s*(\\align[\s\S]*?)\s*\]/g, (match, content) => {
    const escaped = content.replace(/\$/g, '\\$');
    return `$$\n${escaped}\n$$`;
  });

  // Fix simple bracket expressions like [ \text{...} ... ] to $$...$$
  fixed = fixed.replace(/\[\s*([^\[\]]*\\[^\[\]]*)\s*\]/g, (match, content) => {
    // Only replace if it looks like LaTeX (has backslash commands)
    if (content.includes('\\')) {
      const escaped = content.replace(/\$/g, '\\$');
      return `$$${escaped}$$`;
    }
    return match;
  });

  return fixed;
}

// Function to escape dollar signs in currency amounts, but leave LaTeX math intact
export const escapeDollarSigns = (markdownText) => {
  // Use negative lookbehind to avoid double-escaping already escaped dollars
  return markdownText.replace(/(?<!\\)(\$\d+(?:,\d{3})*(?:\.\d+)?)/g, '\\$&');
};

// Function to fix LaTeX-specific issues within math blocks
function fixLatexIssues(mathContent) {
  let fixed = mathContent;

  // Fix single backslashes that should be double backslashes for line breaks in align environments
  // This regex looks for patterns like "\ &" or "\ \end" which indicate line breaks
  fixed = fixed.replace(/([^\\])\\ (&|\\end)/g, '$1\\\\ $2');

  // Fix standalone single backslash at end before closing tags
  fixed = fixed.replace(/([^\\])\\ (\\end\{align)/g, '$1\\\\ $2');

  // Fix malformed dollar signs in LaTeX (like \\$100 should be \$100 or just 100)
  // Remove extra backslashes before dollar signs in math mode
  fixed = fixed.replace(/\\\\(\$\d)/g, '\\$1');

  return fixed;
}

export function formatResult(result, ignoreLatex = false) {
  if (!result) return result;

  const v = removeFollowUpQuestions(result);
  let withFixedLatex = v;
  if (!ignoreLatex) {
    withFixedLatex = fixLatexDelimiters(v);
  }

  // Check if there's LaTeX math in the content
  const hasLatexMath = withFixedLatex.indexOf('$$') > -1 || withFixedLatex.indexOf('\\begin{') > -1;

  // If there's LaTeX math, we need to escape dollar signs OUTSIDE math blocks
  if (hasLatexMath && !ignoreLatex) {
    // Only handle $$ blocks and fix LaTeX issues within them
    const result = withFixedLatex.replace(
      /(\$\$[\s\S]*?\$\$)|(?<!\\)(\$\d+(?:,\d{3})*(?:\.\d+)?)/g,
      (match, mathBlock, currency) => {
        if (mathBlock) {
          // Extract content between $$, fix issues, then reassemble
          const content = mathBlock.slice(2, -2);
          const fixed = fixLatexIssues(content);
          return '$$' + fixed + '$$';
        } else if (currency) {
          return '\\' + currency;
        }
        return match;
      }
    );

    return result;
  }

  return escapeDollarSigns(withFixedLatex);
}

function isCitationLink(href) {
  if (!href) return false;

  const citationMockURL = 'https://example.com/citation';
  const link = href.slice(0, 28);
  return link === citationMockURL;
}

const answerFormatter = new AnswerFormatter();

export const ResultOnly = ({
  result,
  ignoreLatex,
  isShowingCitations,
  onCitationClick,
  enableMoreDetail,
  sourceDocs,
  selectedDocs,
  model,
  userMode,
  allowLinks,
  isRFPMode,
}) => {
  const formattedResult = formatResult(result, ignoreLatex);
  const plugins = React.useMemo(() => answerFormatter.getPluginsForResult(formattedResult), [formattedResult]);
  const forceAllowLinks = true;

  if (!result) return null;

  return (
    <MarkdownStyling>
      <Body5>
        <Markdown
          rehypePlugins={plugins}
          components={{
            li(props) {
              const { node, ...rest } = props;

              if (enableMoreDetail) {
                return (
                  <MoreDetailEnhanced
                    {...rest}
                    selectedDocs={selectedDocs}
                    model={model}
                    userMode={userMode}
                    isRFPMode={isRFPMode}
                    isBullet
                  />
                );
              }
              return <li {...rest} className={'no-detail'} />;
            },
            a(props) {
              const { node, ...rest } = props;
              if (isCitationLink(rest.href)) {
                return (
                  <Citation
                    isShowingCitations={isShowingCitations}
                    data={rest.children}
                    url={rest.href}
                    metadata={rest}
                    onClick={onCitationClick}
                    sourceDocs={sourceDocs}
                  />
                );
              }
              if (allowLinks || forceAllowLinks) {
                return <a {...rest} target="_blank" rel="noopener noreferrer" />;
              }

              return null;
            },
          }}
        >
          {formattedResult}
        </Markdown>
      </Body5>
    </MarkdownStyling>
  );
};

export const JSONAnswer = ({ result, renderer }) => {
  const [prevValidResult, setPrevValidResult] = React.useState(null);

  React.useEffect(() => {
    const list = parseJSONResultCompanyList(result);
    if (list) {
      setPrevValidResult(list);
    }
  }, [result]);

  if (prevValidResult === null) {
    return null;
    // return <Working />;
  }

  return renderer(prevValidResult);
};

export const Answer = ({
  result,
  isAddingCitations,
  isShowingCitations,
  onCitationClick,
  enableMoreDetail,
  selectedDocs,
  sourceDocs,
  model,
  userMode,
  isComplete,
  isRFPMode,
}) => {
  if (result === 'Working...' || !result || result === '') {
    return <Working />;
  }
  return (
    <>
      {isAddingCitations && <AddingCitations />}

      <ResultOnly
        result={result}
        isShowingCitations={isShowingCitations}
        onCitationClick={onCitationClick}
        enableMoreDetail={enableMoreDetail}
        selectedDocs={selectedDocs}
        sourceDocs={sourceDocs}
        model={model}
        userMode={userMode}
        isComplete={isComplete}
        isRFPMode={isRFPMode}
      />
    </>
  );
};
